import { useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import type { FormInstance } from 'antd/lib/form';
import * as yup from 'yup';
import { CHECK_USERNAME_AVAILABILITY } from 'api/auth/queries';
import {
  CheckUsernameAvailability,
  CheckUsernameAvailabilityVariables,
} from 'api/auth/types/CheckUsernameAvailability';

export const USERNAME_SLUG_MATCH_REGEX = /^[a-z0-9._-]*$/;

export const USERNAME_SLUG_NON_EMPTY_REGEX = /^[a-z0-9._-]+$/;

export const USERNAME_INVALID_CHARS_MESSAGE =
  'Username can only contain lowercase letters, numbers, periods, hyphens, and underscores';

export const USERNAME_REQUIRED_MESSAGE = 'User name is required';

export const USERNAME_TAKEN_MESSAGE = 'Username is already taken';

export const USERNAME_AVAILABILITY_CHECK_FAILED_MESSAGE =
  'Failed to check username availability';

export const truncateForFormErrorDisplay = (
  message: string,
  maxLength = 400
): string => {
  const trimmed = message.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength)}…`;
};

let usernameAvailabilityFlag = true;

export const setUsernameAvailability = (value: boolean): void => {
  usernameAvailabilityFlag = value;
};

export const getUsernameAvailability = (): boolean => usernameAvailabilityFlag;

type StringSchemaWithAvailable = yup.StringSchema & {
  available(this: yup.StringSchema, message?: string): yup.StringSchema;
};

yup.addMethod(
  yup.string,
  'available',
  function available(this: yup.StringSchema, message = USERNAME_TAKEN_MESSAGE) {
    return this.test('available', message, (value) => {
      const v = value ?? '';
      if (v.trim() === '') {
        return true;
      }
      if (!USERNAME_SLUG_MATCH_REGEX.test(v)) {
        return true;
      }
      return getUsernameAvailability();
    });
  }
);

export const createUsernameValidation = (): yup.StringSchema => {
  return (
    yup
      .string()
      .matches(
        USERNAME_SLUG_MATCH_REGEX,
        USERNAME_INVALID_CHARS_MESSAGE
      ) as StringSchemaWithAvailable
  )
    .available()
    .required(USERNAME_REQUIRED_MESSAGE) as yup.StringSchema;
};

export const validateManageBioUsernameValue = async (
  value: unknown
): Promise<void> => {
  await createUsernameValidation().validate(value ?? undefined);
};

export const validateManageBioUsernameFormRule = (
  _: unknown,
  value: unknown
): Promise<void> =>
  validateManageBioUsernameValue(value).catch((err: unknown) => {
    if (err instanceof yup.ValidationError) {
      return Promise.reject(new Error(err.errors[0] ?? err.message));
    }
    return Promise.reject(err);
  });

export const MANAGE_BIO_USERNAME_FORM_FIELD = 'username' as const;

const USERNAME_DEBOUNCE_MS = 300;

export const normalizeManageBioUsername = (value: string): string =>
  value.trim().toLowerCase();

type UseManageBioUsernameAvailabilityParams = {
  form: FormInstance;
  storeId: string | undefined;
  initialSlug: string | null | undefined;
};

type UsernameAvailabilityOutcome =
  | { kind: 'available' }
  | { kind: 'taken' }
  | { kind: 'validation'; message: string }
  | { kind: 'error'; message: string };

export const useManageBioUsernameAvailability = ({
  form,
  storeId,
  initialSlug,
}: UseManageBioUsernameAvailabilityParams): {
  scheduleUsernameAvailabilityCheck: (value: unknown) => void;
  ensureUsernameAllowedForSubmit: (
    rawUsername: string | undefined
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
} => {
  const client = useApolloClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const initialNormalizedRef = useRef('');
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    initialNormalizedRef.current = normalizeManageBioUsername(
      initialSlug ?? ''
    );
    setUsernameAvailability(true);
    return () => {
      isMountedRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [initialSlug]);

  const runAvailabilityQuery = useCallback(
    async (
      normalizedUsername: string
    ): Promise<UsernameAvailabilityOutcome> => {
      try {
        const { data } = await client.query<
          CheckUsernameAvailability,
          CheckUsernameAvailabilityVariables
        >({
          query: CHECK_USERNAME_AVAILABILITY,
          variables: {
            input: {
              username: normalizedUsername,
              ...(storeId ? { ownerId: storeId } : {}),
            },
          },
          fetchPolicy: 'network-only',
        });
        const isAvailable =
          data?.checkUsernameAvailability?.isAvailable ?? false;
        return isAvailable ? { kind: 'available' } : { kind: 'taken' };
      } catch (error: unknown) {
        const graphQLErrors = (error as { graphQLErrors?: Array<unknown> })
          ?.graphQLErrors;
        const first = graphQLErrors?.[0] as
          | {
              extensions?: {
                code?: string;
                response?: { message?: string[] };
              };
            }
          | undefined;
        if (first?.extensions?.code === 'BAD_USER_INPUT') {
          const message = first.extensions?.response?.message?.[0];
          return {
            kind: 'validation',
            message:
              typeof message === 'string'
                ? truncateForFormErrorDisplay(message)
                : USERNAME_INVALID_CHARS_MESSAGE,
          };
        }
        return {
          kind: 'error',
          message: USERNAME_AVAILABILITY_CHECK_FAILED_MESSAGE,
        };
      }
    },
    [client, storeId]
  );

  const clearUsernameFieldErrorsAndRevalidate = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }
    form.setFields([{ name: MANAGE_BIO_USERNAME_FORM_FIELD, errors: [] }]);
    form
      .validateFields([MANAGE_BIO_USERNAME_FORM_FIELD])
      .catch(() => undefined);
  }, [form]);

  const applyUsernameFieldOutcome = useCallback(
    (outcome: UsernameAvailabilityOutcome) => {
      if (!isMountedRef.current) {
        return;
      }
      if (outcome.kind === 'taken') {
        setUsernameAvailability(false);
        form.setFields([
          {
            name: MANAGE_BIO_USERNAME_FORM_FIELD,
            errors: [USERNAME_TAKEN_MESSAGE],
          },
        ]);
        return;
      }
      if (outcome.kind === 'validation' || outcome.kind === 'error') {
        setUsernameAvailability(false);
        form.setFields([
          { name: MANAGE_BIO_USERNAME_FORM_FIELD, errors: [outcome.message] },
        ]);
        return;
      }
      setUsernameAvailability(true);
      clearUsernameFieldErrorsAndRevalidate();
    },
    [clearUsernameFieldErrorsAndRevalidate, form]
  );

  const scheduleUsernameAvailabilityCheck = useCallback(
    (value: unknown) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(async () => {
        debounceRef.current = null;
        const str = typeof value === 'string' ? value : '';
        const normalized = normalizeManageBioUsername(str);
        const currentRequest = ++requestIdRef.current;
        if (!normalized) {
          if (currentRequest === requestIdRef.current) {
            setUsernameAvailability(true);
            clearUsernameFieldErrorsAndRevalidate();
          }
          return;
        }
        if (!USERNAME_SLUG_NON_EMPTY_REGEX.test(normalized)) {
          if (currentRequest === requestIdRef.current) {
            setUsernameAvailability(true);
            clearUsernameFieldErrorsAndRevalidate();
          }
          return;
        }
        if (normalized === initialNormalizedRef.current) {
          if (currentRequest === requestIdRef.current) {
            setUsernameAvailability(true);
            clearUsernameFieldErrorsAndRevalidate();
          }
          return;
        }
        setUsernameAvailability(false);
        const outcome = await runAvailabilityQuery(normalized);
        if (currentRequest !== requestIdRef.current || !isMountedRef.current) {
          return;
        }
        applyUsernameFieldOutcome(outcome);
      }, USERNAME_DEBOUNCE_MS);
    },
    [
      applyUsernameFieldOutcome,
      clearUsernameFieldErrorsAndRevalidate,
      runAvailabilityQuery,
    ]
  );

  const ensureUsernameAllowedForSubmit = useCallback(
    async (
      rawUsername: string | undefined
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      requestIdRef.current += 1;
      const normalized = normalizeManageBioUsername(rawUsername ?? '');
      if (!normalized) {
        setUsernameAvailability(true);
        return { ok: false, message: USERNAME_REQUIRED_MESSAGE };
      }
      if (!USERNAME_SLUG_NON_EMPTY_REGEX.test(normalized)) {
        setUsernameAvailability(true);
        return { ok: false, message: USERNAME_INVALID_CHARS_MESSAGE };
      }
      if (normalized === initialNormalizedRef.current) {
        setUsernameAvailability(true);
        return { ok: true };
      }
      setUsernameAvailability(false);
      const outcome = await runAvailabilityQuery(normalized);
      if (outcome.kind === 'available') {
        setUsernameAvailability(true);
        return { ok: true };
      }
      if (outcome.kind === 'taken') {
        setUsernameAvailability(false);
        return { ok: false, message: USERNAME_TAKEN_MESSAGE };
      }
      setUsernameAvailability(false);
      return { ok: false, message: outcome.message };
    },
    [runAvailabilityQuery]
  );

  return {
    scheduleUsernameAvailabilityCheck,
    ensureUsernameAllowedForSubmit,
  };
};
